import { Chip } from '@jotpsych/ui';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Button, Divider, Skeleton, Stack, Typography } from '@mui/material';

import { allergySeverityColor, allergySeverityLabel } from '../labels';
import type { AllergyVM } from '../vm/types';

const INACTIVE_CAP = 3;

interface AllergyPeekListProps {
  allergies: AllergyVM[];
  isLoading?: boolean;
  /** Opens the full edit modal (the peek itself is read-only). */
  onEdit?: () => void;
}

/**
 * Compact hover-peek allergy list for the patient context bar, on the same
 * layout as MedicationPeekList: active rows with color-coded severity chips
 * (severe / life-threatening read as errors at a glance), inactive capped
 * with a count. Read-only; Edit opens the full modal via the host.
 */
export const AllergyPeekList: React.FC<AllergyPeekListProps> = ({
  allergies,
  isLoading = false,
  onEdit,
}) => {
  const active = allergies.filter((allergy) => allergy.isActive);
  const inactive = allergies.filter((allergy) => !allergy.isActive);
  const shownInactive = inactive.slice(0, INACTIVE_CAP);

  return (
    <Box sx={{ minWidth: 400, maxWidth: 600, p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Allergies
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {allergies.length} total · {active.length} active
        </Typography>
        {onEdit && (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onEdit}
            sx={{ textTransform: 'none', minWidth: 'auto' }}
          >
            Edit
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        {isLoading &&
          [0, 1, 2].map((row) => (
            <Skeleton key={row} variant="rounded" height={28} sx={{ my: 0.75 }} />
          ))}
        {!isLoading && allergies.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No allergies recorded.
          </Typography>
        )}
        {active.map((allergy) => (
          <Stack
            key={allergy.id}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', py: 0.75 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                {allergy.allergen}
              </Typography>
              {allergy.reaction && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                  {allergy.reaction}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, mt: 0.25 }}>
              {allergy.isSuggested && (
                <Chip variant="soft" size="xSmall" color="info" label="Suggested" />
              )}
              {allergy.severity && (
                <Chip
                  variant="soft"
                  size="xSmall"
                  color={allergySeverityColor[allergy.severity]}
                  label={allergySeverityLabel[allergy.severity]}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          </Stack>
        ))}

        {inactive.length > 0 && (
          <>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.disabled',
              }}
            >
              Inactive
            </Typography>
            {shownInactive.map((allergy) => (
              <Stack
                key={allergy.id}
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {allergy.allergen}
                  {allergy.reaction && (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {' '}
                      · {allergy.reaction}
                    </Box>
                  )}
                </Typography>
                <Chip
                  variant="soft"
                  size="xSmall"
                  color="default"
                  label="Inactive"
                  sx={{ flexShrink: 0 }}
                />
              </Stack>
            ))}
            {inactive.length > INACTIVE_CAP && (
              <Typography variant="caption" color="text.disabled">
                …and {inactive.length - INACTIVE_CAP} more
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
